import {Filter} from '@github-ui/filter'
import type {FilterQuery} from '@github-ui/filter-core'
import {LABELS} from '@github-ui/issues-shared/labels'
import {BookmarkIcon, DuplicateIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {useCallback, useState} from 'react'

import type {SavedView} from '../../components/views/types'
import {BUTTON_LABELS} from '../../constants/buttons'
import {useDuplicateSavedViewDialog} from '../../hooks/use-duplicate-saved-view-dialog'
import {useSavedViewFilterProviders} from '../../hooks/use-saved-view-filter-providers'
import styles from './SavedViewFilterBar.module.css'

interface SavedViewFilterBarProps {
  owner: string
  repo: string
  /** The view as currently saved on the server — used to detect dirty state and to preserve fields on save. */
  savedView: SavedView
  /** The currently active query (URL or stored). */
  currentQuery: string
  /** Push a new query to the URL (and the live preview). */
  onQueryChange: (query: string) => void
  /** Persist the current dirty query to the saved view. Errors are surfaced at page level. */
  onSaveChanges: () => void
  /** True while a save-changes request is in flight. */
  isSaving: boolean
  /** Whether the viewer can save changes to or duplicate the view. */
  canManage: boolean
}

export function SavedViewFilterBar({
  owner,
  repo,
  savedView,
  currentQuery,
  onQueryChange,
  onSaveChanges,
  isSaving,
  canManage,
}: SavedViewFilterBarProps) {
  const [filterValue, setFilterValue] = useState(currentQuery)
  const [syncedQuery, setSyncedQuery] = useState(currentQuery)
  if (syncedQuery !== currentQuery) {
    setSyncedQuery(currentQuery)
    setFilterValue(currentQuery)
  }

  const filterProviders = useSavedViewFilterProviders({owner, repo})

  const handleSubmit = useCallback((request: FilterQuery) => onQueryChange(request.raw), [onQueryChange])

  const {openDuplicateDialog, duplicateDialog} = useDuplicateSavedViewDialog({owner, repo})

  const trimmedQuery = currentQuery.trim()
  const isDirty = trimmedQuery !== savedView.query.trim()

  const handleSaveAsNewView = useCallback(() => {
    openDuplicateDialog({...savedView, query: trimmedQuery})
  }, [openDuplicateDialog, savedView, trimmedQuery])

  return (
    <div className={styles.filterRow}>
      <div className={styles.filterField}>
        <Filter
          id="saved-view-filter"
          label={LABELS.issueSearchInputAriaLabel}
          placeholder={LABELS.issueSearchInputPlaceholder}
          variant="input"
          providers={filterProviders}
          filterValue={filterValue}
          onChange={setFilterValue}
          onSubmit={handleSubmit}
          settings={{aliasMatching: true, groupAndKeywordSupport: true}}
          showValidationMessage={false}
          visuallyHideLabel
        />
      </div>
      {canManage && (
        <ActionMenu>
          <ActionMenu.Button variant={isDirty ? 'primary' : 'default'} disabled={!isDirty || isSaving}>
            {isSaving ? LABELS.views.saving : BUTTON_LABELS.save}
          </ActionMenu.Button>
          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item onSelect={onSaveChanges}>
                <ActionList.LeadingVisual>
                  <BookmarkIcon />
                </ActionList.LeadingVisual>
                {LABELS.views.saveChangesToView}
              </ActionList.Item>
              <ActionList.Item onSelect={handleSaveAsNewView}>
                <ActionList.LeadingVisual>
                  <DuplicateIcon />
                </ActionList.LeadingVisual>
                {LABELS.views.saveChangesToNewView}
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {duplicateDialog}
    </div>
  )
}
